import { useEffect, useState, type RefObject } from "react";

const TILE_ASPECT_RATIO = 16 / 9;

// Picks the column count that lets `count` tiles at `ratio` fill the most of
// a `width` × `height` box — the standard "video gallery" packing solver
// (the same shape used by conferencing-app grid views). For each candidate
// column count, a tile is capped by whichever dimension runs out first
// (container width ÷ columns, or container height ÷ rows); the candidate
// that maximizes the resulting tile area wins. This replaces the previous
// fixed `{minWidth: 360, max: 3}` responsive config, which reflowed by
// available *width* only and had no notion of using the wall's height at
// all — the actual bug this fixes (one camera floating in a mostly-empty
// viewport).
export function computeAutoColumns(width: number, height: number, count: number, gapPx: number): number {
  if (count <= 0 || width <= 0 || height <= 0) return 1;

  let bestColumns = 1;
  let bestTileArea = 0;
  for (let columns = 1; columns <= count; columns++) {
    const rows = Math.ceil(count / columns);
    const tileWidth = (width - gapPx * (columns - 1)) / columns;
    const tileHeightFromWidth = tileWidth / TILE_ASPECT_RATIO;
    const tileHeightFromHeight = (height - gapPx * (rows - 1)) / rows;
    const tileHeight = Math.min(tileHeightFromWidth, tileHeightFromHeight);
    if (tileHeight <= 0) continue;
    const tileArea = tileHeight * tileHeight * TILE_ASPECT_RATIO;
    if (tileArea > bestTileArea) {
      bestTileArea = tileArea;
      bestColumns = columns;
    }
  }
  return bestColumns;
}

// Buffer below the grid for the Section's own bottom padding plus a little
// breathing room, so the solved layout doesn't sit flush against the
// viewport edge or force a sliver of scroll.
const BOTTOM_BUFFER_PX = 24;

// Re-solves whenever the container resizes (ResizeObserver), the viewport
// resizes (window "resize" — covers height changes that don't affect the
// container's own shrink-wrapped size), or the camera count changes. Only
// meaningful when the grid density control is set to "Auto" — a fixed
// 2/3/4/5 choice from the user always wins over this, see LiveGridPage's own
// `gridColumnsValue`.
//
// Deliberately does NOT use the container element's own rendered height as
// the "available height" input: this element sits inside a normal block/flex
// flow with no explicit height of its own, so its rendered height is just
// however tall its content happens to be — self-referential for a solver
// whose whole job is to decide how tall the content should be. Instead,
// "available height" is derived from where the container's top edge actually
// rendered (`getBoundingClientRect().top`, always accurate regardless of the
// surrounding layout mechanism) against the real viewport height.
export function useAutoGridColumns(containerRef: RefObject<HTMLElement | null>, count: number, gapPx: number): number {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const recompute = () => {
      const rect = el.getBoundingClientRect();
      const availableHeight = Math.max(window.innerHeight - rect.top - BOTTOM_BUFFER_PX, 0);
      setColumns(computeAutoColumns(rect.width, availableHeight, count, gapPx));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(el);
    window.addEventListener("resize", recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [containerRef, count, gapPx]);

  return columns;
}
